/**
 * API Client for Clinic Frontend
 * Wrapper nad /api/clinic/* endpoints
 */

import axios from 'axios'

// VITE_API_BASE_URL should be the Worker API domain (e.g., https://api.genogram.wellysa.com)
// Full API paths are in the functions below (e.g., /api/clinic/pending-patients)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.genogram.wellysa.com'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Authentication
 * TODO: Zaimplementować auth przez /api/clinic/login (JWT/Token)
 */
export const authAPI = {
  /**
   * Login
   * POST /api/clinic/login
   * Body: { username, password } or { email, password }
   */
  login: async (username, password, email = null) => {
    const response = await apiClient.post('/api/clinic/login', {
      username: username || null,
      email: email || null,
      password,
    })
    // Store token if returned
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token)
      // Store user and clinics info
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user))
      }
      if (response.data.clinics && response.data.clinics.length > 0) {
        localStorage.setItem('clinics', JSON.stringify(response.data.clinics))
        // Auto-select first clinic if only one
        if (response.data.clinics.length === 1) {
          localStorage.setItem('clinic_id', response.data.clinics[0].id)
        }
      }
    }
    return response
  },
  
  /**
   * Logout
   * POST /api/clinic/logout
   */
  logout: async () => {
    localStorage.removeItem('auth_token')
    const response = await apiClient.post('/api/clinic/logout')
    return response
  },
  
  /**
   * Check if user is authenticated
   * GET /api/clinic/me
   */
  checkAuth: async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return false
      
      const response = await apiClient.get('/api/clinic/me')
      if (response.status === 200 && response.data) {
        // Update stored user and clinics
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user))
        }
        if (response.data.clinics) {
          localStorage.setItem('clinics', JSON.stringify(response.data.clinics))
        }
        return true
      }
      return false
    } catch {
      return false
    }
  },
}

/**
 * Clinic API
 */
export const clinicAPI = {
  /**
   * Get clinic by ID (for editing)
   * GET /api/clinic/clinics/{id}
   */
  getClinic: async (clinicId) => {
    const response = await apiClient.get(`/api/clinic/clinics/${clinicId}`)
    return response.data
  },
  
  /**
   * Update clinic (edit by clinic)
   * PATCH /api/clinic/clinics/{id}
   */
  updateClinic: async (clinicId, data) => {
    const response = await apiClient.patch(`/api/clinic/clinics/${clinicId}`, data)
    return response.data
  },
  
  /**
   * List pending patients
   * GET /api/clinic/pending-patients?clinic_id=123
   */
  listPendingPatients: async (clinicId) => {
    const response = await apiClient.get('/api/clinic/pending-patients', {
      params: { clinic_id: clinicId },
    })
    return response.data
  },
  
  /**
   * Claim patient
   * POST /api/clinic/claim-patient/{id}?clinic_id=123
   */
  claimPatient: async (submissionId, clinicId) => {
    const response = await apiClient.post(
      `/api/clinic/claim-patient/${submissionId}`,
      {},
      { params: { clinic_id: clinicId } }
    )
    return response.data
  },
  
  /**
   * List claimed patients
   * GET /api/clinic/claimed-patients?clinic_id=123
   */
  listClaimedPatients: async (clinicId) => {
    const response = await apiClient.get('/api/clinic/claimed-patients', {
      params: { clinic_id: clinicId },
    })
    return response.data
  },
  
  /**
   * View patient details
   * GET /api/clinic/patient/{id}?clinic_id=123
   */
  viewPatient: async (submissionId, clinicId) => {
    const response = await apiClient.get(`/api/clinic/patient/${submissionId}`, {
      params: { clinic_id: clinicId },
    })
    return response.data
  },
}

// Add Authorization header to all requests if token exists
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export default apiClient
